import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { useApptFormattedTime } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/hooks/useApptFormattedTime'
import { useApptToShow } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/hooks/useApptToShow'
import { appointmentIsSupported } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { VIDEO_PLATFORM_DISPLAY_NAME } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { RECORDING_STATUS } from '@lyrahealth-inc/shared-app-logic/src/features/videoSession/constants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { ProviderJoinSessionBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerJoinSessionBanner/ProviderJoinSessionBanner'

import { getAuthConfig, getAuthUserId } from '../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { getPrivacyGuardOverride } from '../../../data/privacyGuardSelectors'
import { setPrivacyGuardOverride } from '../../../data/privacyGuardSlice'
import { useAppDispatch } from '../../../data/store'
import { useGetAppointmentsQuery } from '../../../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { clearSelectedClientStore } from '../../../lyraTherapy/clients/clientDetails/data/ltClientDetailsAutoActions'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { getLTVideoSessionOpen, getPrivacyGuardOn } from '../../../lyraTherapy/data/ltVideoSelectors'
import { useCreateVideoSessionMutation } from '../../../lyraTherapy/data/videoSessionApi'
import { setPrivacyGuardOn } from '../../../lyraTherapy/data/videoSessionSlice'
import { CLIENT_HOME } from '../../constants/routingConstants'
import { logToSentry } from '../../utils/utils'

type JoinSessionBannerProps = {
  onZoomSessionJoin: (appointment: Appointment) => void
}

const JoinSessionBanner: FunctionComponent<JoinSessionBannerProps> = ({ onZoomSessionJoin }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const videoSessionOpen = useSelector(getLTVideoSessionOpen)
  const privacyGuardOn = useSelector(getPrivacyGuardOn)
  const privacyGuardOverride = useSelector(getPrivacyGuardOverride)
  const [createVideoSession, { isLoading: isFetchingToken }] = useCreateVideoSessionMutation()
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const selectedClientId = useSelector(getSelectedClientId)
  const { isDAEngageOnOffPlatformVideoEnabled } = useFlags()
  const { data: appointments } = useGetAppointmentsQuery({
    providerId: userId,
    durationRange: { before: { hours: 2 }, after: { hours: 4 } },
    view: CalendarAppointmentsView.CALENDAR,
  })
  const supportedAppointments = useMemo(
    () =>
      appointments?.filter(
        (appt) =>
          appointmentIsSupported(appt, isDAEngageOnOffPlatformVideoEnabled) &&
          appt.meetingFormat === MEETING_FORMATS.VIDEO,
      ) ?? [],
    [appointments, isDAEngageOnOffPlatformVideoEnabled],
  )

  const intl = useIntl()
  const { apptToShow = null } = useApptToShow({ appts: supportedAppointments, reminderWindow: 10 })
  const sessionNumber = apptToShow?.sessionNumber ?? -1
  const { startsIn } = useApptFormattedTime(apptToShow)

  const isExternal = (apptToShow?.videoPlatform ?? DEFAULT_VIDEO_PLATFORM) === VIDEO_PLATFORM.EXTERNAL
  const isExternalAndSupportsOnOffPlatformVideo = isExternal && !!config?.supportsOnOffPlatformVideo
  const isNotExternalAndSupportsOnOffPlatformVideo = !isExternal && !!config?.supportsOnOffPlatformVideo
  const isSessionActive = startsIn === 'now'
  // The provider's explicit decision for this appointment, if they have made one. It is persisted,
  // so it survives a refresh and wins over the default of turning the guard on once the session
  // is active.
  const overrideForAppt =
    privacyGuardOverride && privacyGuardOverride.appointmentId === apptToShow?.appointmentId
      ? privacyGuardOverride.on
      : null
  useEffect(() => {
    if (isExternalAndSupportsOnOffPlatformVideo) {
      dispatch(setPrivacyGuardOn(overrideForAppt ?? isSessionActive))
    }
  }, [isExternalAndSupportsOnOffPlatformVideo, isSessionActive, overrideForAppt, dispatch])

  useEffect(() => {
    if (privacyGuardOn && apptToShow?.userId) {
      const isCorrectClient = selectedClientId === apptToShow.userId
      const isOnClientHome = location.pathname.startsWith(CLIENT_HOME.route)
      if (!isOnClientHome || !isCorrectClient) {
        if (!isCorrectClient) {
          dispatch(clearSelectedClientStore())
        }
        dispatch(setSelectedClientId(apptToShow.userId))
        navigate(CLIENT_HOME.route)
      }
    }
  }, [privacyGuardOn, apptToShow?.userId, selectedClientId, location.pathname, dispatch, navigate])

  useEffect(() => {
    if (isNotExternalAndSupportsOnOffPlatformVideo) {
      dispatch(setPrivacyGuardOn(false))
    }
  }, [isNotExternalAndSupportsOnOffPlatformVideo, dispatch])

  const platformSuffix = isExternalAndSupportsOnOffPlatformVideo
    ? intl.formatMessage(
        {
          defaultMessage: '· Video ({platformName})',
          description: 'Video platform label shown in session banner time row',
        },
        { platformName: VIDEO_PLATFORM_DISPLAY_NAME[VIDEO_PLATFORM.EXTERNAL] },
      )
    : undefined

  const onPress = useCallback(() => {
    if (!userId || !apptToShow?.userInfo) {
      return
    }

    logToSentry('getVideoSessionToken', {
      stack: new Error('getVideoSessionToken call site').stack,
    })
    createVideoSession({
      appointmentId: apptToShow.appointmentId,
      patientId: apptToShow.userInfo.lyraId,
      providerId: userId,
      sessionNumber: sessionNumber,
      ...(config?.recordingDisabled && { recording_status: RECORDING_STATUS.disabled }),
    })
      .unwrap()
      .then(() => {
        onZoomSessionJoin(apptToShow)
      })
  }, [apptToShow, userId, createVideoSession, sessionNumber, onZoomSessionJoin, config?.recordingDisabled])

  const shouldShowBanner = !videoSessionOpen && Boolean(apptToShow?.userInfo)

  useEffect(() => {
    if (!shouldShowBanner && privacyGuardOn) {
      dispatch(setPrivacyGuardOn(false))
    }
  }, [shouldShowBanner, privacyGuardOn, dispatch])

  if (!shouldShowBanner || !apptToShow) {
    return null
  }
  return (
    <ProviderJoinSessionBanner
      sessionStartDate={getDateFromAppointment(apptToShow)}
      sessionDuration={apptToShow.appointmentDuration}
      sessionStartsIn={startsIn}
      onPress={onPress}
      isFetchingToken={isFetchingToken}
      platformSuffix={platformSuffix}
      showPrivacyGuard={isExternalAndSupportsOnOffPlatformVideo}
      privacyGuardOn={privacyGuardOn}
      onPrivacyGuardChange={(on) => {
        dispatch(setPrivacyGuardOn(on))
        dispatch(setPrivacyGuardOverride({ appointmentId: apptToShow.appointmentId, on }))
        if (on && apptToShow?.userId) {
          if (selectedClientId !== apptToShow.userId) {
            dispatch(clearSelectedClientStore())
          }
          dispatch(setSelectedClientId(apptToShow.userId))
          navigate(CLIENT_HOME.route)
        }
      }}
    />
  )
}

export default JoinSessionBanner
